// Status-driven grouping for the Grouped table view.
//
// Four locked groups:
//   needs_attention            — overdue, revision-requested, manual
//                                RAG Red override, rejected (rework
//                                required — NOT completed).
//   awaiting_customer_action   — submitted, under-review, derived late,
//                                conditionally-accepted (delivery happened
//                                but is queued for a customer decision or
//                                customer follow-through).
//   upcoming                   — not-started, future / no-due.
//   completed                  — accepted, closed only.
//
// Manual RAG Red override: when ragRating==='red' AND (ragSource is
// 'manual' OR ragSource is undefined — legacy records default to
// manual), the deliverable routes to needs_attention regardless of
// status. Derived red (ragSource==='derived') does NOT override —
// derived red comes from auto-derivation and the status pipeline
// already routes the underlying state correctly.
//
// Recurring parent surfacing: when a surfaced child is supplied, the
// parent places via the surfaced child's state (manual Red on the
// parent still overrides). This keeps the Grouped view honest — a
// monthly contract whose current occurrence is overdue surfaces in
// needs_attention rather than upcoming.
//
// No dedicated "Recurring" group — parents distribute across the four
// state groups via surfacing, per locked spec.

import type { Deliverable } from "../../../../types";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveSurfacing } from "./deriveSurfacing";
import type { ChildRow, ParentRow, Row } from "./groupDeliverables";

export type DeliverableGroupKey =
  | "needs_attention"
  | "awaiting_customer_action"
  | "upcoming"
  | "completed";

export const GROUP_ORDER: ReadonlyArray<DeliverableGroupKey> = [
  "needs_attention",
  "awaiting_customer_action",
  "upcoming",
  "completed",
];

export const GROUP_TITLE: Record<DeliverableGroupKey, string> = {
  needs_attention: "Needs attention",
  awaiting_customer_action: "Awaiting customer action",
  upcoming: "Upcoming",
  completed: "Completed",
};

export interface DeliverableGroup {
  key: DeliverableGroupKey;
  title: string;
  /** Dynamic sub-label (e.g. "1 overdue · 2 revision requested · 1 RAG red").
   *  Empty string when the bucket has no items. */
  subLabel: string;
  items: Deliverable[];
}

/** Read-default: ragRating === 'red' AND (ragSource === 'manual' OR
 *  ragSource is undefined) is treated as manual Red. Derived red is
 *  the auto-derivation result and does NOT trigger the override. */
function hasManualRedOverride(d: Deliverable): boolean {
  if (d.ragRating !== "red") return false;
  return d.ragSource === "manual" || d.ragSource === undefined;
}

/** Place a single deliverable in the four-group layout.
 *
 *  @param d            The deliverable to classify.
 *  @param today        Reference date for derived-status (overdue/late).
 *  @param surfacedChild Optional. When supplied, a recurring parent
 *                      places via this child's state instead of its own.
 *                      Manual Red on the parent still overrides. */
export function placeInGroup(
  d: Deliverable,
  today: Date,
  surfacedChild?: Deliverable,
): DeliverableGroupKey {
  if (hasManualRedOverride(d)) return "needs_attention";
  const target = surfacedChild ?? d;
  const derived = deriveDeliverableStatus(
    target.status,
    target.dueDate,
    today,
    target.submittedAt,
  );
  switch (derived) {
    case "overdue":
    case "revision-requested":
    case "rejected":
    case "awaiting-resubmission":
      return "needs_attention";
    case "submitted":
    case "under-review":
    case "late":
    case "conditionally-accepted":
      return "awaiting_customer_action";
    case "accepted":
    case "closed":
      return "completed";
    case "not-started":
      return "upcoming";
  }
}

const MS_PER_DAY = 86_400_000;

function isoDay(iso: string | undefined): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function daysOverdue(d: Deliverable, todayIso: string): number {
  const todayMs = isoDay(todayIso);
  const dueMs = isoDay(d.dueDate);
  if (todayMs === null || dueMs === null) return 0;
  if (dueMs >= todayMs) return 0;
  return Math.round((todayMs - dueMs) / MS_PER_DAY);
}

function sortNeedsAttention(
  a: Deliverable,
  b: Deliverable,
  todayIso: string,
): number {
  // Most-overdue first; ties fall through to status priority then id.
  return daysOverdue(b, todayIso) - daysOverdue(a, todayIso);
}

function sortUpcoming(a: Deliverable, b: Deliverable): number {
  // Ascending dueDate; rows without a dueDate sink to the bottom.
  const ad = a.dueDate ?? "";
  const bd = b.dueDate ?? "";
  if (ad === bd) return 0;
  if (!ad) return 1;
  if (!bd) return -1;
  return ad.localeCompare(bd);
}

function sortAwaiting(a: Deliverable, b: Deliverable): number {
  // Older submissions first — the customer review queue is FIFO.
  const as = a.submittedAt ?? "";
  const bs = b.submittedAt ?? "";
  if (as === bs) return 0;
  if (!as) return 1;
  if (!bs) return -1;
  return as.localeCompare(bs);
}

function sortCompleted(a: Deliverable, b: Deliverable): number {
  // Most-recently-accepted first — recent wins surface, history sinks.
  const aa = a.acceptedAt ?? a.updatedAt;
  const bb = b.acceptedAt ?? b.updatedAt;
  return (bb ?? "").localeCompare(aa ?? "");
}

function sortWithinGroup(
  key: DeliverableGroupKey,
  items: Deliverable[],
  todayIso: string,
): Deliverable[] {
  const copy = [...items];
  if (key === "needs_attention") {
    copy.sort((a, b) => sortNeedsAttention(a, b, todayIso));
  } else if (key === "upcoming") {
    copy.sort(sortUpcoming);
  } else if (key === "awaiting_customer_action") {
    copy.sort(sortAwaiting);
  } else {
    copy.sort(sortCompleted);
  }
  return copy;
}

interface AttentionCounts {
  overdue: number;
  revisionRequested: number;
  rejected: number;
  manualRed: number;
}

function countAttention(
  items: Deliverable[],
  today: Date,
): AttentionCounts {
  let overdue = 0;
  let revisionRequested = 0;
  let rejected = 0;
  let manualRed = 0;
  for (const d of items) {
    // Manual Red is counted on its own line even when it also overlaps
    // with another flavour — the manager-set signal is the dominant
    // reason the row is in this bucket.
    if (hasManualRedOverride(d)) {
      manualRed += 1;
      continue;
    }
    if (d.status === "revision-requested") {
      revisionRequested += 1;
      continue;
    }
    if (d.status === "rejected") {
      rejected += 1;
      continue;
    }
    const derived = deriveDeliverableStatus(
      d.status,
      d.dueDate,
      today,
      d.submittedAt,
    );
    if (derived === "overdue") overdue += 1;
  }
  return { overdue, revisionRequested, rejected, manualRed };
}

function buildAttentionSubLabel(counts: AttentionCounts): string {
  const parts: string[] = [];
  if (counts.overdue > 0) parts.push(`${counts.overdue} overdue`);
  if (counts.revisionRequested > 0) {
    parts.push(`${counts.revisionRequested} revision requested`);
  }
  if (counts.rejected > 0) parts.push(`${counts.rejected} rejected`);
  if (counts.manualRed > 0) parts.push(`${counts.manualRed} RAG red`);
  return parts.join(" · ");
}

function countAwaiting(
  items: Deliverable[],
  today: Date,
): { submitted: number; underReview: number } {
  let submitted = 0;
  let underReview = 0;
  for (const d of items) {
    const derived = deriveDeliverableStatus(
      d.status,
      d.dueDate,
      today,
      d.submittedAt,
    );
    // `late` collapses into the submitted count — they're still in the
    // submitted slice of the review queue, just with a timing flag.
    if (derived === "submitted" || derived === "late") submitted += 1;
    else if (derived === "under-review") underReview += 1;
  }
  return { submitted, underReview };
}

function buildAwaitingSubLabel(items: Deliverable[], today: Date): string {
  const { submitted, underReview } = countAwaiting(items, today);
  const parts: string[] = [];
  if (submitted > 0) parts.push(`${submitted} submitted`);
  if (underReview > 0) parts.push(`${underReview} under review`);
  return parts.join(" · ");
}

function pluralise(noun: string, n: number): string {
  return n === 1 ? noun : `${noun}s`;
}

function buildSubLabel(
  key: DeliverableGroupKey,
  items: Deliverable[],
  today: Date,
): string {
  if (items.length === 0) return "";
  if (key === "needs_attention") {
    return buildAttentionSubLabel(countAttention(items, today));
  }
  if (key === "awaiting_customer_action") {
    return buildAwaitingSubLabel(items, today);
  }
  if (key === "upcoming") return `${items.length} ahead`;
  return `${items.length} ${pluralise("deliverable", items.length)}`;
}

/** Caller-facing wrapper that composes the dynamic sublabel for a
 *  group from raw deliverables. Empty groups return "" so callers can
 *  elide the header. */
export function groupLabel(
  key: DeliverableGroupKey,
  items: Deliverable[],
  todayIso: string,
): string {
  return buildSubLabel(key, items, new Date(`${todayIso}T00:00:00Z`));
}

/** Row-aware bucket: each entry preserves the parent/child/one-off
 *  Row shape so the renderer can keep recurring children attached to
 *  their parents inside the placement group. */
export interface DeliverableRowGroup {
  key: DeliverableGroupKey;
  title: string;
  subLabel: string;
  rows: Row[];
}

/** Resolve the surfaced child Deliverable for a ParentRow via the
 *  v3 deriveSurfacing rule. Returns null when the schedule has no
 *  surfaced occurrence (empty children or fully-terminal). */
function surfacedChildOf(row: ParentRow, today: Date): Deliverable | null {
  const surfacing = deriveSurfacing(row, today);
  if (surfacing.surfacedOccurrenceId === null) return null;
  const match = row.children.find(
    (c: ChildRow) => c.deliverable.id === surfacing.surfacedOccurrenceId,
  );
  return match ? match.deliverable : null;
}

/** Place a Row in a group. Parent rows route via deriveSurfacing
 *  (v3 rule — overdue-priority oldest-first; fully-terminal schedules
 *  collapse to `completed`); one-off and orphan rows place by their
 *  own deliverable.
 *
 *  Empty schedules (totalOccurrences === 0) are NOT routed to
 *  Completed even though deriveSurfacing reports phase=completed for
 *  them — semantically, an empty schedule is a brand-new recurrence
 *  rule the user hasn't generated occurrences for yet. The Generate
 *  Occurrences CTA lives on the row and must stay visible (i.e. the
 *  row must not be collapsed under the Completed bucket). Route empty
 *  schedules via the parent's own state instead. */
function placeRow(row: Row, today: Date): DeliverableGroupKey {
  if (row.kind === "parent") {
    // Manual Red on the parent itself still wins — kept consistent with
    // placeInGroup.
    if (hasManualRedOverride(row.deliverable)) return "needs_attention";
    const surfacing = deriveSurfacing(row, today);
    const hasChildren = surfacing.totalOccurrences > 0;
    if (hasChildren && surfacing.phase === "completed") {
      // Every child terminal — the parent row belongs in the
      // completed bucket regardless of its own stored status.
      return "completed";
    }
    const surfaced =
      surfacing.surfacedOccurrenceId !== null
        ? row.children.find(
            (c) => c.deliverable.id === surfacing.surfacedOccurrenceId,
          )?.deliverable
        : undefined;
    if (!surfaced) {
      // Empty schedule, or non-completed phase without a surfaced id
      // (data inconsistency) — fall back to the parent's own state.
      return placeInGroup(row.deliverable, today);
    }
    return placeInGroup(row.deliverable, today, surfaced);
  }
  return placeInGroup(row.deliverable, today);
}

/** When a recurring parent has children spanning multiple group states,
 *  emit the parent in EVERY group its children touch — each instance
 *  carries only the children that match that group, while `allChildren`
 *  preserves the full schedule for the OccurrenceStrip.
 *
 *  Manual RAG Red on the parent skips the distribution: the schedule
 *  routes as a unit to needs_attention with all children attached. The
 *  manager's flag is the dominant signal; splitting children across
 *  groups would dilute it.
 *
 *  Empty-schedule parents (zero children) also skip distribution —
 *  there's nothing to distribute. They place by their own state. */
function distributeParent(
  row: ParentRow,
  today: Date,
): Array<{ key: DeliverableGroupKey; row: ParentRow }> {
  // Manual red wins — whole schedule routes to needs_attention.
  if (hasManualRedOverride(row.deliverable)) {
    return [{ key: "needs_attention", row }];
  }
  // No children → keep the single-row placement (via placeRow's parent
  // fallback). Preserves the Generate Occurrences CTA visibility.
  if (row.children.length === 0) {
    return [{ key: placeRow(row, today), row }];
  }
  // Distribute children by their individual placement.
  const childBuckets: Record<DeliverableGroupKey, ChildRow[]> = {
    needs_attention: [],
    awaiting_customer_action: [],
    upcoming: [],
    completed: [],
  };
  for (const c of row.children) {
    childBuckets[placeInGroup(c.deliverable, today)].push(c);
  }
  const allChildren = row.allChildren ?? row.children;
  const out: Array<{ key: DeliverableGroupKey; row: ParentRow }> = [];
  for (const key of GROUP_ORDER) {
    if (childBuckets[key].length === 0) continue;
    out.push({
      key,
      row: {
        kind: "parent",
        deliverable: row.deliverable,
        children: childBuckets[key],
        allChildren,
      },
    });
  }
  return out;
}

export function groupRowsByStatus(
  rows: readonly Row[],
  todayIso: string,
): DeliverableRowGroup[] {
  const today = new Date(`${todayIso}T00:00:00Z`);
  const buckets: Record<DeliverableGroupKey, Row[]> = {
    needs_attention: [],
    awaiting_customer_action: [],
    upcoming: [],
    completed: [],
  };
  for (const row of rows) {
    if (row.kind === "parent") {
      for (const { key, row: parentInstance } of distributeParent(
        row,
        today,
      )) {
        buckets[key].push(parentInstance);
      }
    } else {
      buckets[placeRow(row, today)].push(row);
    }
  }
  return GROUP_ORDER.map((key) => {
    // For sub-label aggregation we flatten the rows into their
    // representative deliverables — counts match what users see at the
    // bucket header. Parents contribute the surfaced child where
    // available; otherwise the parent's own record.
    const flat: Deliverable[] = buckets[key].map((r) => {
      if (r.kind === "parent") {
        const surfaced = surfacedChildOf(r, today);
        return surfaced ?? r.deliverable;
      }
      return r.deliverable;
    });
    return {
      key,
      title: GROUP_TITLE[key],
      subLabel: buildSubLabel(key, flat, today),
      rows: buckets[key],
    };
  });
}

export function groupDeliverablesByStatus(
  items: readonly Deliverable[],
  todayIso: string,
): DeliverableGroup[] {
  const today = new Date(`${todayIso}T00:00:00Z`);
  const buckets: Record<DeliverableGroupKey, Deliverable[]> = {
    needs_attention: [],
    awaiting_customer_action: [],
    upcoming: [],
    completed: [],
  };
  for (const d of items) {
    buckets[placeInGroup(d, today)].push(d);
  }
  return GROUP_ORDER.map((key) => ({
    key,
    title: GROUP_TITLE[key],
    subLabel: buildSubLabel(key, buckets[key], today),
    items: sortWithinGroup(key, buckets[key], todayIso),
  }));
}
